import {useState} from "react";

/**
 * Hover + focus reveal intent for a hint that must be reachable by pointer AND by keyboard/touch
 * (WCAG 1.4.13 — content on hover/focus must not be hover-only): the hint is `active` while the
 * pointer is over the trigger OR the trigger holds focus, so a keyboard tab-in or a touch tap (which
 * focuses the control) reveals it just like a mouse hover does.
 */
export interface RevealIntentState {
	/** True while the hint should be revealed — pointer over the trigger OR the trigger is focused. */
	active: boolean;
	/** Pointer entered the trigger (mouseenter). */
	show: () => void;
	/** Pointer left the trigger (mouseleave). */
	hide: () => void;
	/** Trigger gained keyboard/touch focus. */
	focus: () => void;
	/** Trigger lost focus (blur). */
	blur: () => void;
}

/**
 * Tracks hover and focus independently so the hint stays revealed while EITHER is engaged: hovering
 * then tabbing away (or focusing then moving the mouse off) does not flicker the hint shut until both
 * intents have cleared. Pure client state — safe to call during SSG (no browser globals touched).
 */
export function useRevealIntent(): RevealIntentState {
	const [hovered, setHovered] = useState(false);
	const [focused, setFocused] = useState(false);
	return {
		active: hovered || focused,
		show: () => setHovered(true),
		hide: () => setHovered(false),
		focus: () => setFocused(true),
		blur: () => setFocused(false),
	};
}
